/**
 * StateOrchestrator — 12 상태 전이 관리 + burst queue pause/resume + motion · exit 재생.
 *
 * Plan 05-05 Task 2.
 *
 * 역할:
 *   - transitionTo(next) : 다음 상태로 전이. 현재 렌더된 element exit 애니메이션 → 새 element enter.
 *   - pauseQueue() / resumeQueue() : goal-reached / paused 시 burst queue 중단 · 재개 훅.
 *     (실제 queue 는 events/handlers 에서 planBurst 로 처리, 여기서는 paused 플래그만)
 *
 * paused 의미 (DESIGN §3.1 #12):
 *   - viewer 모드: 완전 숨김 (render.ts shouldRenderForViewer=false).
 *   - preview 모드: 반투명 오버레이.
 *   - paused 중 도착하는 donation 이벤트는 내부 큐에 쌓고, resume 시 한꺼번에 flush.
 *     (Wave 0 는 단순 skip — handlers 의 pauseQueue flag 참조하여 transitionTo 무시.)
 *
 * Motion 해결 (DESIGN §10.4):
 *   - resolveMotion(theme.motionPreset, reducedMotion) — reducedMotion=true 시 snap 강등.
 *   - reducedMotion 은 getter 로 주입 → 런타임 토글 반영.
 */
import type { Theme } from '@loop/shared/theme';
import { playEnter, playExit, resolveMotion } from '../motion/presets.js';
import { renderState } from '../states/render.js';
import type { WidgetState } from '../states/types.js';
import type { ViewerMode } from '../viewer-mode.js';

export interface StateOrchestratorOptions {
  readonly host: HTMLElement;
  readonly theme: Theme;
  readonly mode: ViewerMode;
  getReducedMotion(): boolean;
}

export interface StateController {
  transitionTo(next: WidgetState): Promise<void>;
  pauseQueue(): void;
  resumeQueue(): void;
}

export class StateOrchestrator implements StateController {
  private currentEl: HTMLElement | null = null;
  private paused = false;

  constructor(private readonly opts: StateOrchestratorOptions) {}

  isPaused(): boolean {
    return this.paused;
  }

  pauseQueue(): void {
    this.paused = true;
  }

  resumeQueue(): void {
    this.paused = false;
  }

  async transitionTo(next: WidgetState): Promise<void> {
    const nextEl = renderState(next, this.opts.theme, this.opts.mode);
    this.opts.host.appendChild(nextEl);

    if (nextEl.children.length > 0) {
      const motion = resolveMotion(this.opts.theme.motionPreset, this.opts.getReducedMotion());
      try {
        await playEnter(nextEl, motion).finished;
      } catch {
        // fill:forwards 중단 등 무시
      }
    }

    const prev = this.currentEl;
    this.currentEl = nextEl;
    if (prev && prev !== nextEl) {
      try {
        const motion = resolveMotion(this.opts.theme.motionPreset, this.opts.getReducedMotion());
        const exitAnim = playExit(prev, motion);
        exitAnim.onfinish = (): void => {
          prev.remove();
        };
      } catch {
        prev.remove();
      }
    }
  }
}
